/**
 * MesRepasAdultePanel — items 12.5/12.6, côté intéressé.
 *
 * **Le principe voulu par la mairie** (arbitrage B) : « chacun qui va sur sa
 * vue administrée de la cantine, comme s'il voulait mettre son enfant, il
 * peut se mettre lui ». Si l'inscription passait par les managers, ils
 * passeraient l'année à saisir les repas de leurs agents.
 *
 * **Les mêmes règles que pour un enfant** (arbitrage J) : on planifie à
 * l'avance sur des jours récurrents, la cantine doit être ouverte ce jour-là,
 * et passé le délai de prévenance réglé par la commune (24 ou 48 h), on ne
 * bouge plus — la cuisine a compté.
 *
 * Ce panneau n'apparaît que pour le personnel : un habitant n'a rien à y faire.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  api, ApiException,
  type CantineConvive, type CantineRepasAdulte, type CategorieConvive,
} from '@/lib/api';
import { Card, Button, Badge, Alert } from '@/components/ui';
import { AlertCircle, UtensilsCrossed, Check } from 'lucide-react';

const JOURS = [
  { n: 1, l: 'Lundi' }, { n: 2, l: 'Mardi' }, { n: 3, l: 'Mercredi' },
  { n: 4, l: 'Jeudi' }, { n: 5, l: 'Vendredi' },
];

const CATEGORIES: Array<{ v: CategorieConvive; l: string }> = [
  { v: 'agent', l: 'Agent de la commune' },
  { v: 'enseignant', l: 'Enseignant' },
  { v: 'elu', l: 'Élu' },
  { v: 'exterieur', l: 'Extérieur' },
];

const CHAMP = 'h-9 rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink focus:border-accent focus:outline-none';

const euros = (c: number | null) =>
  c == null ? '—' : `${(c / 100).toFixed(2).replace('.', ',')} €`;

function dateFr(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', weekday: 'short', day: 'numeric', month: 'short',
  }).format(new Date(`${iso}T12:00:00`));
}

/** Les 6 prochaines semaines : l'horizon utile pour se déclarer. */
function fenetre(): { from: string; to: string } {
  const auj = new Date();
  const fin = new Date(auj.getTime() + 42 * 86400_000);
  return { from: auj.toISOString().slice(0, 10), to: fin.toISOString().slice(0, 10) };
}

export function MesRepasAdultePanel() {
  const [convive, setConvive] = useState<CantineConvive | null>(null);
  const [repas, setRepas] = useState<CantineRepasAdulte[]>([]);
  const [cutoff, setCutoff] = useState(24);
  const [chargement, setChargement] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [categorie, setCategorie] = useState<CategorieConvive>('agent');
  const [jours, setJours] = useState<number[]>([]);
  const [debut, setDebut] = useState(fenetre().from);
  const [fin, setFin] = useState(fenetre().to);
  const [busy, setBusy] = useState(false);
  const [joursOuvertsFenetre, setJoursOuvertsFenetre] = useState<number | null>(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const r = await api.scolaire.adultes.moi();
      setConvive(r.convive);
      if (r.convive) {
        const { from, to } = fenetre();
        const cal = await api.scolaire.adultes.repas(r.convive.id, from, to);
        setRepas(cal.repas);
        setCutoff(cal.cutoffHours);
        setJoursOuvertsFenetre(cal.joursOuverts);
      }
    } catch {
      setConvive(null);   // pas de droit sur ce service : le panneau se tait
    } finally { setChargement(false); }
  }, []);

  useEffect(() => { void charger(); }, [charger]);

  async function agir(fn: () => Promise<unknown>, ok: string) {
    setBusy(true); setError(null); setMsg(null);
    try { await fn(); setMsg(ok); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.payload.error : 'Action impossible'); }
    finally { setBusy(false); }
  }

  if (chargement) return null;

  return (
    <Card variant="glass" padded>
      <h3 className="mb-1 flex items-center gap-1.5 text-sm font-medium text-ink">
        <UtensilsCrossed className="h-4 w-4" aria-hidden="true" /> Mes repas à la cantine
      </h3>
      <p className="mb-3 text-xs text-ink-muted">
        Vous déclarez vos propres repas, comme vous inscririez votre enfant.
        La cuisine compte les couverts à l'avance : prévenez au moins
        {' '}<strong className="text-ink">{cutoff} h</strong> avant le service.
      </p>

      {error && <Alert variant="error" icon={AlertCircle}>{error}</Alert>}
      {msg && <Alert variant="success" icon={Check}>{msg}</Alert>}

      {/* ── Pas encore inscrit : on s'inscrit soi-même ─────────────────────── */}
      {!convive ? (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-sm text-ink">
              Je suis :{' '}
              <select value={categorie} onChange={(e) => setCategorie(e.target.value as CategorieConvive)}
                aria-label="Ma catégorie" className={CHAMP}>
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </label>
          </div>
          <div>
            <p className="mb-1 text-xs text-ink-muted">Mes jours habituels (facultatif) :</p>
            <div className="flex flex-wrap gap-1.5">
              {JOURS.map((j) => (
                <label key={j.n} className="flex items-center gap-1 rounded-full border border-border/60 px-2 py-0.5 text-xs text-ink">
                  <input type="checkbox" checked={jours.includes(j.n)}
                    onChange={(e) => setJours(e.target.checked
                      ? [...jours, j.n] : jours.filter((x) => x !== j.n))} />
                  {j.l}
                </label>
              ))}
            </div>
          </div>
          {jours.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 text-sm text-ink">
              <label>Du <input type="date" value={debut} onChange={(e) => setDebut(e.target.value)} className={CHAMP} /></label>
              <label>au <input type="date" value={fin} onChange={(e) => setFin(e.target.value)} className={CHAMP} /></label>
            </div>
          )}
          <Button variant="primary" size="sm" disabled={busy}
            onClick={() => void (async () => {
              setBusy(true); setError(null); setMsg(null);
              try {
                const r = await api.scolaire.adultes.inscrire({
                  categorie,
                  joursSemaine: jours,
                  ...(jours.length ? { dateDebut: debut, dateFin: fin } : {}),
                });
                // On annonce le compte RÉEL. Un zéro silencieux ressemblait à
                // une panne (bug prod du 27/07) : désormais il s'explique.
                if (!jours.length) {
                  setMsg('Vous êtes inscrit. Cochez maintenant les jours où vous mangez.');
                } else if (r.repasPoses > 0) {
                  setMsg(`Vous êtes inscrit — ${r.repasPoses} repas posé${r.repasPoses > 1 ? 's' : ''} sur les jours d'ouverture de la période.`);
                } else {
                  setMsg('Vous êtes inscrit, mais aucun repas n\'a pu être posé : la cantine n\'a aucun jour d\'ouverture sur la période choisie (vacances, ou calendrier pas encore ouvert par la mairie). Vos jours seront à poser quand le calendrier ouvrira.');
                }
                await charger();
              } catch (e) {
                setError(e instanceof ApiException ? e.payload.error : 'Inscription impossible');
              } finally { setBusy(false); }
            })()}>
            M'inscrire à la cantine
          </Button>
        </div>
      ) : (
        <>
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <Badge variant="info" size="sm">
              {CATEGORIES.find((c) => c.v === convive.categorie)?.l ?? convive.categorie}
            </Badge>
            <Button variant="ghost" size="sm" disabled={busy}
              onClick={() => void agir(() => api.scolaire.adultes.radier(convive.id),
                'Inscription retirée. Vos repas à venir ont été annulés.')}>
              Me retirer de la cantine
            </Button>
          </div>

          {repas.length === 0 ? (
            <p className="text-sm text-ink-muted">
              {joursOuvertsFenetre === 0
                ? 'La cantine n\'a aucun jour d\'ouverture sur les six prochaines semaines (vacances, ou calendrier pas encore ouvert par la mairie) — c\'est pour cela qu\'aucun repas n\'apparaît.'
                : 'Aucun repas posé sur les six prochaines semaines.'}
            </p>
          ) : (
            <ul className="space-y-1">
              {repas.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center gap-2 rounded-r-md border border-border/60 px-2.5 py-1.5">
                  <span className="text-sm text-ink">{dateFr(r.dateService)}</span>
                  {r.statut === 'absent'
                    ? <Badge variant="neutral" size="sm">Pas de repas</Badge>
                    : <Badge variant="success" size="sm">Repas réservé</Badge>}
                  <span className="text-xs text-ink-muted">{euros(r.prixCentimes)}</span>
                  {r.modifiable ? (
                    <Button variant="ghost" size="sm" className="ml-auto" disabled={busy}
                      onClick={() => void agir(
                        () => api.scolaire.adultes.basculer({
                          conviveId: convive.id, dateService: r.dateService,
                          present: r.statut === 'absent',
                        }),
                        r.statut === 'absent' ? 'Repas ajouté.' : 'Repas retiré.')}>
                      {r.statut === 'absent' ? 'Je mange' : 'Je ne mange pas'}
                    </Button>
                  ) : (
                    <span className="ml-auto text-xs text-ink-muted">
                      Délai dépassé — voyez le service scolaire
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </Card>
  );
}
